// ============================================
// pages/society/SocietyRequests.tsx
// Society raises gunny requests; views DM review status; PO link once converted.
// ============================================
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AlertCircle, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { gunnyRequestsAPI, seasonsAPI, commoditiesAPI, godownsAPI } from '../../api/services';
import { useAuth } from '../../contexts/AuthContext';
import type { Season } from '../../types/markfed';
import { BAG_TYPE_OPTIONS, DEFAULT_BAG_TYPE, bagTypeLabel } from '../../components/gunny/bagTypes';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

interface ItemForm {
  bag_type: string;
  specification: string;
  requested_bags: number;
  commodity_id: number;
}

const emptyItem = (): ItemForm => ({ bag_type: DEFAULT_BAG_TYPE, specification: '', requested_bags: 0, commodity_id: 0 });

const emptyForm = (seasonId: number) => ({
  season_id: seasonId,
  required_by_date: '',
  delivery_godown_id: 0,
  notes: '',
  items: [emptyItem()] as ItemForm[],
});

export const SocietyRequests: React.FC = () => {
  const { user } = useAuth();
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [reqs, setReqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm(0));
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [search, setSearch] = useState<string>('');

  const load = useCallback(async (sid: number) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await gunnyRequestsAPI.list(sid);
      setReqs(data);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load requests');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const districtId = user?.district_id || undefined;
        const [act, lst, com, gdn] = await Promise.all([
          seasonsAPI.active().catch(() => ({ data: null as any })),
          seasonsAPI.list().catch(() => ({ data: [] as Season[] })),
          commoditiesAPI.list().catch(() => ({ data: [] as any[] })),
          godownsAPI.list(districtId).catch(() => ({ data: [] as any[] })),
        ]);
        const list = Array.isArray(lst.data) ? lst.data : [];
        setSeasons(list);
        setCommodities((Array.isArray(com.data) ? com.data : []).filter((c: any) => c.is_active !== false));
        setGodowns(Array.isArray(gdn.data) ? gdn.data : []);
        const activeId = act.data?.id || list.find((s) => s.is_active)?.id || list[0]?.id || 0;
        if (activeId) {
          setSeasonId(activeId);
          setForm(emptyForm(activeId));
          load(activeId);
        } else {
          setLoading(false);
        }
      } catch {
        setLoading(false);
      }
    })();
  }, [load, user?.district_id]);

  const totalBags = form.items.reduce((s, it) => s + (Number(it.requested_bags) || 0), 0);

  const updateItem = (idx: number, field: keyof ItemForm, value: any) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx: number) => setForm((f) => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
  }));

  const submit = async () => {
    const sid = form.season_id || seasonId;
    if (!sid) { setErr('Please select a season'); return; }
    const validItems = form.items
      .map((it) => ({
        bag_type: it.bag_type,
        specification: it.specification?.trim() || undefined,
        requested_bags: Number(it.requested_bags) || 0,
        commodity_id: it.commodity_id || undefined,
      }))
      .filter((it) => it.requested_bags > 0);
    if (validItems.length === 0) { setErr('Add at least one line item with a quantity'); return; }
    setSubmitting(true); setErr(null);
    try {
      await gunnyRequestsAPI.create(sid, {
        required_by_date: form.required_by_date || undefined,
        delivery_godown_id: form.delivery_godown_id || undefined,
        notes: form.notes?.trim() || undefined,
        items: validItems,
      });
      setFlash('Request submitted to DM for review.');
      setShowModal(false);
      setForm(emptyForm(seasonId || 0));
      if (seasonId) await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not submit request');
    } finally { setSubmitting(false); }
  };

  const filteredReqs = useMemo(() => {
    const s = search.trim().toLowerCase();
    return reqs.filter((r: any) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (commodityFilter > 0) {
        const has = r.commodity_id === commodityFilter
          || (r.items || []).some((it: any) => it.commodity_id === commodityFilter);
        if (!has) return false;
      }
      if (s) {
        const blob = `${r.id} ${r.po?.po_number || ''} ${(r.items || []).map((it: any) => `${it.bag_type} ${it.specification || ''} ${it.commodity?.name || ''}`).join(' ')}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [reqs, statusFilter, commodityFilter, search]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(filteredReqs, 25);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gunny Requests</h1>
          <p className="text-sm text-gray-600 mt-1">Request bags from the District Manager. They may approve as-is, adjust the quantity, or reject.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={seasonId || 0}
            onChange={(e) => {
              const v = Number(e.target.value) || 0;
              setSeasonId(v);
              setForm((f) => ({ ...f, season_id: v }));
              if (v) load(v); else setReqs([]);
            }}
            className="px-3 py-1.5 text-sm border rounded bg-white">
            {seasons.length === 0 && <option value={0}>No seasons</option>}
            {seasons.map((s: any) => (
              <option key={s.id} value={s.id}>{s.season_name}{s.is_active ? ' (active)' : ''}</option>
            ))}
          </select>
          <button onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded">
            <Plus className="w-4 h-4" /> New Request
          </button>
        </div>
      </div>

      <div className="mb-4 bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">All statuses</option>
          <option value="pending">Pending DM review</option>
          <option value="dm_approved">Approved</option>
          <option value="dm_modified">Approved (modified)</option>
          <option value="rejected">Rejected</option>
          <option value="converted_to_po">Converted to PO</option>
        </select>
        <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value) || 0)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value={0}>All commodities</option>
          {commodities.map((c: any) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <input type="text" placeholder="Search request id / PO no / bag / commodity…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded flex-1 min-w-[200px]" />
        <span className="text-xs text-gray-500">{filteredReqs.length} of {reqs.length} requests</span>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {flash && <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm"><CheckCircle className="w-4 h-4" /> {flash}</div>}

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-gray-500">Loading…</div>
          : reqs.length === 0 ? <div className="p-10 text-center text-gray-500">No requests yet. Click <span className="font-medium">New Request</span> above.</div>
          : filteredReqs.length === 0 ? <div className="p-10 text-center text-gray-500">No requests match the current filters.</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Items</th>
                  <th className="px-3 py-2">Required by</th>
                  <th className="px-3 py-2">Drop point</th>
                  <th className="px-3 py-2 text-right">Requested</th>
                  <th className="px-3 py-2 text-right">DM Approved</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">PO</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((r, i) => {
                  const items: any[] = Array.isArray(r.items) && r.items.length > 0 ? r.items : [];
                  const sno = (page - 1) * pageSize + i + 1;
                  return (
                    <tr key={r.id} className="border-b last:border-0 align-top">
                      <td className="px-3 py-2">
                        <div className="text-gray-700">{sno}</div>
                        <div className="text-[10px] text-gray-400 font-mono">#{r.id}</div>
                      </td>
                      <td className="px-3 py-2 max-w-xs">
                        {items.length > 0 ? (
                          <ul className="space-y-0.5">
                            {items.map((it: any) => (
                              <li key={it.id} className="flex items-baseline gap-2">
                                {it.commodity?.name && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">{it.commodity.name}</span>
                                )}
                                <span className="text-gray-800">{bagTypeLabel(it.bag_type)}</span>
                                {it.specification && <span className="text-xs text-gray-500">· {it.specification}</span>}
                                <span className="ml-auto text-xs text-gray-500 tabular-nums">× {Number(it.requested_bags).toLocaleString()}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-gray-700">{bagTypeLabel(r.bag_type)}{r.specification ? ` · ${r.specification}` : ''}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{r.required_by_date || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.delivery_godown?.godown_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.requested_bags?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.dm_approved_bags != null ? r.dm_approved_bags.toLocaleString() : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                          r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          r.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                          r.status === 'converted_to_po' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {String(r.status).replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{r.po?.po_number || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        {!loading && reqs.length > 0 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="requests"
            />
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-900">Request Gunny Bags</h2>
              <p className="text-xs text-gray-500 mt-0.5">DM will review and approve / adjust before placing PO.</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
                  <select value={form.season_id || ''}
                    onChange={(e) => setForm({ ...form, season_id: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded text-sm bg-white">
                    {seasons.length === 0 && <option value="">No seasons available</option>}
                    {seasons.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.season_name}{s.is_active ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Required by</label>
                  <input type="date" value={form.required_by_date}
                    onChange={(e) => setForm({ ...form, required_by_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drop point (godown)</label>
                  <select value={form.delivery_godown_id || 0}
                    onChange={(e) => setForm({ ...form, delivery_godown_id: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded text-sm bg-white">
                    <option value={0}>—</option>
                    {godowns.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.godown_name}{g.godown_type ? ` (${String(g.godown_type).toUpperCase()})` : ''}
                      </option>
                    ))}
                  </select>
                  {godowns.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">No godowns in your district yet — ask the DM to add one.</p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Line items</label>
                  <button type="button" onClick={addItem}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded">
                    <Plus className="w-3 h-3" /> Add item
                  </button>
                </div>
                <div className="border border-gray-200 rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-gray-600">
                        <th className="px-2 py-1.5">Commodity</th>
                        <th className="px-2 py-1.5">Bag type</th>
                        <th className="px-2 py-1.5">Specification</th>
                        <th className="px-2 py-1.5 text-right">Bags</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">
                            <select value={it.commodity_id || 0}
                              onChange={(e) => updateItem(i, 'commodity_id', Number(e.target.value) || 0)}
                              className="w-full px-2 py-1 border rounded text-xs bg-white">
                              <option value={0}>—</option>
                              {commodities.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <select value={it.bag_type}
                              onChange={(e) => updateItem(i, 'bag_type', e.target.value)}
                              className="w-full px-2 py-1 border rounded text-xs bg-white">
                              {BAG_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <input type="text" placeholder="e.g. B-Twill, 50 kg"
                              value={it.specification}
                              onChange={(e) => updateItem(i, 'specification', e.target.value)}
                              className="w-full px-2 py-1 border rounded text-xs" />
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" min={1} value={it.requested_bags || ''}
                              onChange={(e) => updateItem(i, 'requested_bags', Number(e.target.value) || 0)}
                              className="w-full px-2 py-1 border rounded text-xs text-right" />
                          </td>
                          <td className="px-2 py-1 text-center">
                            {form.items.length > 1 && (
                              <button type="button" onClick={() => removeItem(i)}
                                className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-gray-50">
                        <td colSpan={3} className="px-2 py-1.5 text-right font-medium text-gray-700">Total</td>
                        <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{totalBags.toLocaleString()}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
              <button onClick={submit} disabled={submitting || totalBags <= 0}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50">
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyRequests;
